/**
 * How the dashboard writes dates, times and numbers: one formatter for every page.
 *
 * Dates and times read the Belgian way, in Belgian time: "21/10/2026 18:05"
 * (day/month/year, 24-hour clock, Europe/Brussels), in every language and
 * whatever the browser's own language or time zone. Calendar days the API
 * sends as "2026-10-15" (already counted in Belgian time) are written as they
 * are, never shifted by a time zone.
 *
 * Numbers, percentages, weekday names and relative times follow the
 * dashboard's language: "1.234,5" and "71%" in Dutch and French, "1,234.5" in
 * English.
 */

import { getLanguage, type Language } from "@/lib/i18n/language";

/** De Ceuster and Velta work in Belgium; the API counts its days there too. */
export const TIME_ZONE = "Europe/Brussels";

/** The locale each language formats numbers, weekdays and relative times with. */
export const LOCALES: Record<Language, string> = {
  nl: "nl-BE",
  en: "en-GB",
  fr: "fr-BE",
};

/**
 * The thousands separator where the language's locale differs from Belgian
 * usage: fr-BE groups with a narrow space ("1 234"), but De Ceuster writes
 * "1.234" in French as in Dutch.
 */
const GROUP_SEPARATOR: Partial<Record<Language, string>> = { fr: "." };

/** Gives the parts of "21/10/2026 18:05"; en-GB orders them like nl-BE. */
const DATE_TIME_PARTS = new Intl.DateTimeFormat("en-GB", {
  timeZone: TIME_ZONE,
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
});

const CALENDAR_DAY = /^(\d{4})-(\d{2})-(\d{2})$/;

/** A moment as the API sends it (an ISO timestamp), or a Date. */
export type Moment = string | number | Date | null | undefined;

interface DateTimeParts {
  day: string;
  month: string;
  year: string;
  hour: string;
  minute: string;
}

/** The hour (0-23) in Belgian time, whatever the browser's own time zone. */
export function belgianHour(at: Date = new Date()): number {
  const hour = DATE_TIME_PARTS.formatToParts(at).find((part) => part.type === "hour");
  return Number(hour?.value ?? at.getHours());
}

/** A moment as "21/10/2026 18:05" in Belgian time; `fallback` when missing or invalid. */
export function formatDateTime(value: Moment, fallback = "—"): string {
  const parts = belgianParts(value);
  return parts
    ? `${parts.day}/${parts.month}/${parts.year} ${parts.hour}:${parts.minute}`
    : fallback;
}

/** A moment's date as "21/10/2026" in Belgian time; `fallback` when missing or invalid. */
export function formatDate(value: Moment, fallback = "—"): string {
  const parts = belgianParts(value);
  return parts ? `${parts.day}/${parts.month}/${parts.year}` : fallback;
}

/**
 * A moment's calendar day in Belgian time, as the API writes days:
 * "2026-10-25" for 22:30 UTC on 24 October in summer time; null when missing
 * or invalid.
 */
export function belgianDay(value: Moment): string | null {
  const parts = belgianParts(value);
  return parts ? `${parts.year}-${parts.month}-${parts.day}` : null;
}

/** A moment's time as "18:05" in Belgian time; `fallback` when missing or invalid. */
export function formatTime(value: Moment, fallback = "—"): string {
  const parts = belgianParts(value);
  return parts ? `${parts.hour}:${parts.minute}` : fallback;
}

/** A calendar day ("2026-10-15") as "15/10/2026"; anything else as it came. */
export function formatDay(day: string): string {
  const match = CALENDAR_DAY.exec(day);
  return match ? `${match[3]}/${match[2]}/${match[1]}` : day;
}

/** A calendar day ("2026-10-15") as "15/10", for chart axes; anything else as it came. */
export function formatDayMonth(day: string): string {
  const match = CALENDAR_DAY.exec(day);
  return match ? `${match[3]}/${match[2]}` : day;
}

/**
 * A calendar day ("2026-11-15") written out in the dashboard's language, for
 * a screen reader's name of a day in a calendar: "15 november 2026", "15
 * November 2026", "15 novembre 2026". Anything else as it came.
 */
export function formatLongDay(day: string, language: Language = getLanguage()): string {
  const match = CALENDAR_DAY.exec(day);
  if (!match) {
    return day;
  }
  // Counted in UTC, so the browser's time zone can never move it to another day.
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  return new Intl.DateTimeFormat(LOCALES[language], {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(date);
}

/**
 * A calendar day's short weekday in the dashboard's language: "do" for
 * "2026-10-15" in Dutch, "Thu" in English, "jeu." in French; "" when it is not
 * a day.
 */
export function weekdayOf(day: string, language: Language = getLanguage()): string {
  const match = CALENDAR_DAY.exec(day);
  if (!match) {
    return "";
  }
  // Counted in UTC, so the browser's time zone can never move it to another day.
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  return new Intl.DateTimeFormat(LOCALES[language], { weekday: "short", timeZone: "UTC" }).format(
    date,
  );
}

const CALENDAR_MONTH = /^(\d{4})-(0[1-9]|1[0-2])$/;

/**
 * A calendar month as the API writes it ("2026-11") in the dashboard's
 * language: "november 2026", "November 2026", "novembre 2026" in full;
 * "nov 2026", "Nov 2026", "nov. 2026" short. Anything else as it came.
 */
export function formatMonth(
  month: string,
  style: "long" | "short" = "long",
  language: Language = getLanguage(),
): string {
  const match = CALENDAR_MONTH.exec(month);
  if (!match) {
    return month;
  }
  // Counted in UTC, so the browser's time zone can never move it to another month.
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, 1));
  return new Intl.DateTimeFormat(LOCALES[language], {
    month: style,
    year: "numeric",
    timeZone: "UTC",
  }).format(date);
}

/**
 * A calendar month's name alone, without its year, in the dashboard's
 * language: "november", "November", "novembre" for "2026-11". Anything else
 * as it came.
 */
export function formatMonthName(month: string, language: Language = getLanguage()): string {
  const match = CALENDAR_MONTH.exec(month);
  if (!match) {
    return month;
  }
  // Counted in UTC, so the browser's time zone can never move it to another month.
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, 1));
  return new Intl.DateTimeFormat(LOCALES[language], { month: "long", timeZone: "UTC" }).format(
    date,
  );
}

/** Words joined the language's way: "An en Bram", "An, Bram and Lies", "An, Bram et Lies". */
export function formatList(words: readonly string[], language: Language = getLanguage()): string {
  return new Intl.ListFormat(LOCALES[language], { type: "conjunction" }).format(words);
}

/**
 * A count with its noun in the right number: "1 failed call", "2 failed calls".
 * English nouns only; translated text counts through the i18n plural keys.
 */
export function countLabel(count: number, noun: string, plural = `${noun}s`): string {
  return `${formatNumber(count)} ${count === 1 ? noun : plural}`;
}

interface NumberOptions {
  /** Digits after the decimal comma; whole numbers stay whole. Default 0 to 2, as needed. */
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
}

/**
 * A number in the dashboard's language: "1.234,5" in Dutch and French,
 * "1,234.5" in English. Up to two decimals unless told otherwise.
 */
export function formatNumber(
  value: number,
  options: NumberOptions = {},
  language: Language = getLanguage(),
): string {
  const parts = new Intl.NumberFormat(LOCALES[language], {
    maximumFractionDigits: 2,
    ...options,
  }).formatToParts(value);
  const group = GROUP_SEPARATOR[language];
  return parts.map((part) => (part.type === "group" && group ? group : part.value)).join("");
}

/**
 * A share (0.71) as a whole percentage, "71%", with no space before the sign
 * in any language (the De Ceuster figure style).
 */
export function formatPercent(share: number, language: Language = getLanguage()): string {
  return `${formatNumber(Math.round(share * 100), {}, language)}%`;
}

/**
 * A share of a count, such as failed calls of all calls, in whole percent as
 * `formatPercent`, but never rounded to a claim it does not make: a share
 * above 0 under half a percent reads "<1%", and one under 1 from 99,5% up
 * reads ">99%". So one failure in 300 calls never shows as "0%".
 */
export function formatShare(share: number, language: Language = getLanguage()): string {
  if (share > 0 && share < 0.005) {
    return `<${formatPercent(0.01, language)}`;
  }
  if (share < 1 && share >= 0.995) {
    return `>${formatPercent(0.99, language)}`;
  }
  return formatPercent(share, language);
}

/** The units a relative time is told in, largest first, with their length in seconds. */
const RELATIVE_UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ["year", 365 * 24 * 3600],
  ["month", 30 * 24 * 3600],
  ["week", 7 * 24 * 3600],
  ["day", 24 * 3600],
  ["hour", 3600],
  ["minute", 60],
  ["second", 1],
];

/**
 * How long ago (or how soon) a moment is, in the dashboard's language: "3
 * minuten geleden", "3 minutes ago", "il y a 3 minutes"; "gisteren",
 * "yesterday", "hier" for a day. `fallback` when the moment is missing or invalid.
 */
export function formatRelativeTime(
  value: Moment,
  now: Date = new Date(),
  fallback = "—",
  language: Language = getLanguage(),
): string {
  if (value === null || value === undefined || value === "") {
    return fallback;
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return fallback;
  }
  const seconds = Math.round((date.getTime() - now.getTime()) / 1000);
  const [unit, size] = RELATIVE_UNITS.find(([, length]) => Math.abs(seconds) >= length) ?? [
    "second",
    1,
  ];
  return new Intl.RelativeTimeFormat(LOCALES[language], { numeric: "auto" }).format(
    Math.round(seconds / size),
    unit,
  );
}

/** The moment's day, month, year, hour and minute in Belgian time, or null. */
function belgianParts(value: Moment): DateTimeParts | null {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  const parts: DateTimeParts = { day: "", month: "", year: "", hour: "", minute: "" };
  for (const part of DATE_TIME_PARTS.formatToParts(date)) {
    if (part.type in parts) {
      parts[part.type as keyof DateTimeParts] = part.value;
    }
  }
  return parts;
}
